import React, { useCallback, useEffect, useState } from "react";
import Header from '../../component/Header';
import Sidebar from '../../component/SideBar';
import { useNotifications } from "../../context/NotificationContext";
import moment from "moment";
import { Button, Pagination } from "@mui/material";
import {
    fetchNotifications,
    markNotificationAsRead,
} from "../../service/notificationService";
import { toast } from "react-toastify";

const NotificationsList = () => {
    const [openSidebarToggle, setOpenSidebarToggle] = useState(false)
    const [page, setPage] = useState(1);
    const [pageNotifications, setPageNotifications] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageLoading, setPageLoading] = useState(false);
    const notificationsPerPage = 10;
    const {
        refreshVersion,
        socketConnected,
        socketError,
        notificationPermission,
        pushStatus,
        enableBrowserNotifications,
        refreshNotifications,
    } = useNotifications();
    
    const OpenSidebar = () => {
        setOpenSidebarToggle(!openSidebarToggle)
    }

    const loadNotifications = useCallback(async (nextPage = page) => {
        setPageLoading(true);

        try {
            const response = await fetchNotifications({
                page: nextPage,
                limit: notificationsPerPage,
            });

            setPageNotifications(response.notifications);
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            console.error("Error loading notifications page:", error);
        } finally {
            setPageLoading(false);
        }
    }, [page]);

    const handleMarkAsRead = useCallback(async (notificationId) => {
        try {
            await markNotificationAsRead(notificationId);
            toast.success("Notification marked as read.");
            await refreshNotifications();
            await loadNotifications(page);
        } catch (error) {
            console.error("Error marking notification as read:", error);
            toast.error(
                error?.response?.data?.message || "Failed to mark notification as read."
            );
        }
    }, [loadNotifications, page, refreshNotifications]);

    useEffect(() => {
        loadNotifications(page);
    }, [loadNotifications, page, refreshVersion]);

    const totalPages = Math.max(1, Math.ceil(totalCount / notificationsPerPage));
 
    return (
        <div className='grid-container'>
            <Header OpenSidebar={OpenSidebar} />
            <Sidebar openSidebarToggle={openSidebarToggle} OpenSidebar={OpenSidebar} />
            <main className='main-container notifications-page'>
                <div className="notifications-page-header">
                    <div>
                        <h3>Notifications</h3>
                        <p>Live updates refresh automatically every 15 seconds.</p>
                    </div>
                    <Button
                        variant="outlined"
                        onClick={async () => {
                            await refreshNotifications();
                            await loadNotifications(page);
                        }}
                    >
                        Refresh
                    </Button>
                </div>

                <div className="notifications-status-bar">
                    <span className={`notifications-status-pill ${socketConnected ? "is-live" : "is-offline"}`}>
                        {socketConnected ? "Realtime connected" : "Realtime disconnected"}
                    </span>
                    <span className={`notifications-status-pill ${notificationPermission === "granted" ? "is-live" : "is-offline"}`}>
                        {notificationPermission === "granted"
                            ? "Desktop notifications enabled"
                            : notificationPermission === "unsupported"
                                ? "Desktop notifications unsupported"
                                : "Desktop notifications disabled"}
                    </span>
                    <span className={`notifications-status-pill ${pushStatus === "subscribed" ? "is-live" : "is-offline"}`}>
                        {pushStatus === "subscribed"
                            ? "Browser push subscribed"
                            : pushStatus === "not-configured"
                                ? "Push backend not configured"
                                : pushStatus === "unsupported"
                                    ? "Push unsupported"
                                    : pushStatus === "registering"
                                        ? "Subscribing to push"
                                        : pushStatus === "error"
                                            ? "Push setup failed"
                                            : "Push not enabled"}
                    </span>
                    {notificationPermission !== "granted" && notificationPermission !== "unsupported" && (
                        <Button variant="contained" onClick={enableBrowserNotifications}>
                            Enable desktop and push notifications
                        </Button>
                    )}
                </div>

                {socketError && (
                    <div className="notifications-error-banner">{socketError}</div>
                )}

                {pageLoading && pageNotifications.length === 0 ? (
                    <div className="notifications-empty-state">Loading notifications...</div>
                ) : pageNotifications.length === 0 ? (
                    <div className="notifications-empty-state">No notifications available.</div>
                ) : (
                    <>
                    <div className="notifications-list">
                        {pageNotifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`notification-card ${notification.isRead ? "is-read" : "is-unread"}`}
                            >
                                <div className="notification-card-content">
                                    <div className="notification-card-meta">
                                        <span className="notification-status-pill">
                                            {notification.isRead ? "Read" : "Unread"}
                                        </span>
                                        <span className="notification-card-time">
                                            {notification.createdAt
                                                ? moment(notification.createdAt).format("DD MMM YYYY, hh:mm A")
                                                : "Recently"}
                                        </span>
                                    </div>
                                    <h4>{notification.title}</h4>
                                    <p>{notification.body || "No additional details provided."}</p>
                                    {(notification.customerName || notification.customerEmail) && (
                                        <div className="notification-customer-meta">
                                            {notification.customerName && (
                                                <span><strong>Customer:</strong> {notification.customerName}</span>
                                            )}
                                            {notification.customerEmail && (
                                                <span><strong>Email:</strong> {notification.customerEmail}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="notification-card-actions">
                                    {!notification.isRead && (
                                        <Button
                                            variant="contained"
                                            onClick={() => handleMarkAsRead(notification.id)}
                                        >
                                            Mark as Read
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <div className="notifications-pagination">
                            <Pagination
                                count={totalPages}
                                page={page}
                                color="primary"
                                shape="rounded"
                                onChange={(event, value) => setPage(value)}
                            />
                        </div>
                    )}
                    </>
                )}
            </main>
        </div>
    )
}

export default NotificationsList;
